import { t } from "ttag";

import { useListChannelsQuery, useListUserRecipientsQuery } from "metabase/api";
import { ChannelSettingsBlock } from "metabase/notifications/channels/ChannelSettingsBlock";
import { EmailChannelEdit } from "metabase/notifications/channels/EmailChannelEdit";
import { SlackChannelFieldNew } from "metabase/notifications/channels/SlackChannelFieldNew";
import {
  type ChannelToAddOption,
  NotificationChannelsAddMenu,
} from "metabase/notifications/modals/components/NotificationChannelsAddMenu";
import { getNotificationHandlersGroupedByTypes } from "metabase/notifications/utils";
import { useSelector } from "metabase/redux";
import { canAccessSettings, getUser } from "metabase/selectors/user";
import { Stack } from "metabase/ui";
import type {
  ChannelApiResponse,
  NotificationHandler,
  User,
} from "metabase-types/api";

const DEFAULT_CHANNELS_CONFIG = {
  email: {
    get name() {
      return t`Email`;
    },
    type: "email",
  },
  slack: {
    get name() {
      return t`Slack`;
    },
    type: "slack",
  },
  http: {
    get name() {
      return t`Http`;
    },
    type: "http",
  },
};

interface NotificationChannelsPickerProps {
  notificationHandlers: NotificationHandler[];
  channels: ChannelApiResponse["channels"] | undefined;
  onChange: (newHandlers: NotificationHandler[]) => void;
  emailRecipientText: string;
  getInvalidRecipientText: (domains: string) => string;
}

export const NotificationChannelsPicker = ({
  notificationHandlers,
  channels: nullableChannels,
  onChange,
  getInvalidRecipientText,
}: NotificationChannelsPickerProps) => {
  const { data: httpChannelsConfig = [] } = useListChannelsQuery();
  const { data: users } = useListUserRecipientsQuery();
  const user = useSelector(getUser);
  const userCanAccessSettings = useSelector(canAccessSettings);

  const usersListOptions: User[] = users?.data || (user ? [user] : []);

  const addChannel = (channel: ChannelToAddOption) => {
    let newChannel: NotificationHandler;

    switch (channel.type) {
      case "channel/http": {
        newChannel = {
          channel_type: channel.type,
          channel_id: channel.channel_id,
          recipients: [],
        };
        break;
      }

      case "channel/email": {
        newChannel = {
          channel_type: channel.type,
          recipients: user
            ? [
                {
                  type: "notification-recipient/user",
                  user_id: user.id,
                  details: null,
                },
              ]
            : [],
        };
        break;
      }

      case "channel/slack": {
        newChannel = {
          channel_type: channel.type,
          recipients: [],
        };
        break;
      }
    }

    onChange(notificationHandlers.concat(newChannel));
  };

  const onChannelChange = (
    oldConfig: NotificationHandler,
    newConfig: NotificationHandler,
  ) => {
    const updatedChannels = notificationHandlers.map((value) =>
      value === oldConfig ? newConfig : value,
    );

    onChange(updatedChannels);
  };

  const onRemoveChannel = (channel: NotificationHandler) => {
    const updatedChannels = notificationHandlers.filter(
      (value) => value !== channel,
    );

    onChange(updatedChannels);
  };

  // Default to show the default channels until full formInput is loaded
  const channels = (nullableChannels ||
    DEFAULT_CHANNELS_CONFIG) as ChannelApiResponse["channels"];

  const { emailHandler, slackHandler, hookHandlers } =
    getNotificationHandlersGroupedByTypes(notificationHandlers);

  return (
    <Stack gap="xl" align="start">
      <Stack gap="xl" align="start" data-testid="alert-configured-channel">
        {channels.email?.configured && !!emailHandler && (
          <ChannelSettingsBlock
            title={t`Email`}
            iconName="mail"
            onRemoveChannel={() => onRemoveChannel(emailHandler)}
          >
            <EmailChannelEdit
              channel={emailHandler}
              users={usersListOptions}
              invalidRecipientText={getInvalidRecipientText}
              onChange={(newConfig) => onChannelChange(emailHandler, newConfig)}
            />
          </ChannelSettingsBlock>
        )}

        {channels.slack?.configured && !!slackHandler && (
          <ChannelSettingsBlock
            title={t`Slack`}
            iconName="int"
            onRemoveChannel={() => onRemoveChannel(slackHandler)}
          >
            <SlackChannelFieldNew
              channel={slackHandler}
              channelSpec={channels.slack}
              onChange={(newConfig) => onChannelChange(slackHandler, newConfig)}
            />
          </ChannelSettingsBlock>
        )}

        {userCanAccessSettings &&
          hookHandlers &&
          hookHandlers.map((channel) => (
            <ChannelSettingsBlock
              key={`webhook-${channel.channel_id}`}
              title={
                httpChannelsConfig.find(({ id }) => id === channel.channel_id)
                  ?.name || t`Webhook`
              }
              iconName="webhook"
              onRemoveChannel={() => onRemoveChannel(channel)}
            />
          ))}
      </Stack>

      <NotificationChannelsAddMenu
        notificationHandlers={notificationHandlers}
        channelsSpec={channels}
        httpChannelsConfig={httpChannelsConfig}
        onAddChannel={addChannel}
        userCanAccessSettings={userCanAccessSettings}
      />
    </Stack>
  );
};
